import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import api, { meliAPI } from '../services/api';
import { toast } from 'sonner';
import { shortSku } from '../utils/sku';
import { Settings, Key, X, Link2, CheckCircle2 } from 'lucide-react';

export default function SettingsPage() {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [meliStatus, setMeliStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [diagnostics, setDiagnostics] = useState(null);
  const [apiKey, setApiKey] = useState(null);
  const [yuju, setYuju] = useState(null);
  const [yujuForm, setYujuForm] = useState({ client_id: '', secret_key: '', channel_id: '' });
  const [connecting, setConnecting] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [queueItems, setQueueItems] = useState(null);
  const [queueLoading, setQueueLoading] = useState(false);
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [pricehubForm, setPricehubForm] = useState({ url: '', token: '', canal: 'E-CORP (Meli)' });
  const [pricehubConfiguring, setPricehubConfiguring] = useState(false);
  const [appSettings, setAppSettings] = useState(null);

  const loadQueue = async () => {
    setQueueLoading(true);
    try {
      const { data } = await api.get('/yuju/queue');
      setQueueItems(data.items || []);
      setSelectedSkus([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron consultar los envíos guardados');
    } finally {
      setQueueLoading(false);
    }
  };

  const loadYuju = React.useCallback(async () => {
    try {
      const { data } = await api.get('/yuju/status');
      setYuju(data);
    } catch { /* the card renders its disconnected state */ }
  }, []);
  React.useEffect(() => { loadYuju(); }, [loadYuju]);

  const saveYujuCredentials = async () => {
    setConnecting(true);
    try {
      await api.post('/yuju/credentials', yujuForm);
      toast.success('Credenciales guardadas. Ahora autoriza MeliHub desde Yuju.');
      loadYuju();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron guardar');
    } finally {
      setConnecting(false);
    }
  };

  const loadSettings = React.useCallback(async () => {
    try {
      const { data } = await api.get('/settings');
      setAppSettings(data);
      setPricehubForm({
        url: data.pricehub_url || '',
        token: data.pricehub_token || '',
        canal: data.pricehub_canal || 'E-CORP (Meli)',
      });
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  }, []);
  React.useEffect(() => { loadSettings(); }, [loadSettings]);

  const savePricehubSettings = async () => {
    setPricehubConfiguring(true);
    try {
      // Spread the settings already loaded so this save doesn't blank out
      // tax_rate/currency/ads_monthly, which the same endpoint also owns.
      await api.post('/settings', {
        ...appSettings,
        pricehub_url: pricehubForm.url,
        pricehub_token: pricehubForm.token,
        pricehub_canal: pricehubForm.canal,
        cogs_con_iva: appSettings?.cogs_con_iva !== false,
      });
      toast.success('Configuración de PriceHub guardada');
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setPricehubConfiguring(false);
    }
  };

  const saveCogsSetting = async (value) => {
    const next = { ...(appSettings || {}), cogs_con_iva: value };
    setAppSettings(next);
    try {
      await api.post('/settings', next);
      toast.success('Preferencia de COGS guardada');
    } catch (err) {
      setAppSettings(current => ({ ...(current || {}), cogs_con_iva: !value }));
      toast.error(err.response?.data?.detail || 'No se pudo guardar la preferencia');
    }
  };

  const saveIvaCreditSetting = async (key, value) => {
    const next = { ...(appSettings || {}), [key]: value };
    setAppSettings(next);
    try {
      await api.post('/settings', next);
      toast.success('Preferencia de IVA acreditable guardada');
    } catch (err) {
      setAppSettings(current => ({ ...(current || {}), [key]: !value }));
      toast.error(err.response?.data?.detail || 'No se pudo guardar la preferencia');
    }
  };

  const savePhotoFixSettings = async (nextValues) => {
    const next = { ...(appSettings || {}), ...nextValues };
    setAppSettings(next);
    try {
      await api.post('/settings', next);
      toast.success('Preferencias de fotos guardadas');
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron guardar las preferencias de fotos');
      loadSettings();
    }
  };

  const pushToYuju = async () => {
    setPushing(true);
    try {
      const { data } = await api.post('/yuju/push-content', {});
      if (data.enviados) toast.success(`${data.enviados} publicaciones aceptadas por la API de Yuju`);
      if (!data.enviados && !data.fallidos?.length) toast.info(data.mensaje || 'No hay contenido pendiente');
      if (data.fallidos?.length) {
        toast.error(`${data.fallidos.length} quedaron pendientes: ${data.fallidos[0].error}`);
      }
      loadYuju();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar a Yuju');
    } finally {
      setPushing(false);
    }
  };
  const retryRecentYuju = async () => {
    if (!selectedSkus.length) return;
    setPushing(true);
    try {
      const { data } = await api.post('/yuju/push-content', { reintentar: true, skus: selectedSkus });
      if (data.enviados) toast.success(`${data.enviados} publicaciones aceptadas por la API de Yuju`);
      if (!data.enviados && !data.fallidos?.length) toast.info('No hay envíos guardados para reintentar');
      if (data.fallidos?.length) toast.error(`${data.fallidos.length} siguen pendientes: ${data.fallidos[0].error}`);
      loadYuju();
      await loadQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reintentar en Yuju');
    } finally {
      setPushing(false);
    }
  };
  const [issuing, setIssuing] = useState(false);

  const issueKey = async () => {
    setIssuing(true);
    try {
      const { data } = await api.post('/partner/key');
      setApiKey(data.api_key);
      toast.success('Llave generada. La anterior quedó desactivada.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo generar la llave');
    } finally {
      setIssuing(false);
    }
  };
  const [diagnosing, setDiagnosing] = useState(false);

  const handleDiagnostics = async () => {
    setDiagnosing(true);
    try {
      const [meli, order] = await Promise.all([
        api.get('/diagnostics/meli'),
        api.get('/diagnostics/order-sample').catch(err => ({
          data: { error: err.response?.data?.detail || err.message },
        })),
      ]);
      setDiagnostics({ meli: meli.data, orden_de_ejemplo: order.data });
      toast.success('Diagnóstico completado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al ejecutar el diagnóstico');
    } finally {
      setDiagnosing(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [userRes, meliRes] = await Promise.all([
          api.get('/auth/me'),
          api.get('/meli/status').catch((err) => {
            console.error('Error loading Mercado Libre status:', err);
            return { data: { connected: false } };
          }),
        ]);
        setUser(userRes.data);
        setSubscription(null);
        setMeliStatus(meliRes.data);
      } catch (err) {
        console.error('Error loading settings:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleConnectMeli = async () => {
    const stored = localStorage.getItem('meli_user');
    if (!user?.id && !stored) {
      toast.error('No se pudo identificar tu cuenta. Vuelve a iniciar sesión.');
      return;
    }
    window.location.href = await meliAPI.getAuthUrl(user?.country || 'MX');
  };

  const handleResetPassword = async () => {
    try {
      await api.post('/auth/forgot-password', { email: user?.email });
      toast.success(`Se enviará un correo a ${user?.email} con instrucciones para restablecer tu contraseña.`);
    } catch {
      toast.error('Error al enviar correo de restablecimiento');
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('¿Estás seguro de que quieres cancelar tu suscripción?')) return;
    try {
      await api.post('/subscriptions/cancel');
      toast.success('Suscripción cancelada');
    } catch {
      toast.error('Error al cancelar suscripción');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Configuración" icon={Settings} backTo="/dashboard">
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Configuración" icon={Settings} backTo="/dashboard">
      <div className="max-w-2xl">
        {/* Account Info */}
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-6">Información de la Cuenta</h2>
          <div className="space-y-4">
            {[
              { label: 'Nombre', value: user?.name || '-' },
              { label: 'Email', value: user?.email || '-' },
              { label: 'Sitio de Mercado Libre', value: user?.meli_site || 'MLM' },
              { label: 'ID de Usuario MeLi', value: user?.meli_user_id || '-' },
            ].map(item => (
              <div key={item.label} className="flex items-center justify-between py-3 border-b border-slate-800/50 last:border-0">
                <span className="text-sm text-slate-400">{item.label}</span>
                <span className="text-sm text-white font-medium">{item.value}</span>
              </div>
            ))}
          </div>

          {/* Reset Password */}
          <div className="mt-6">
            <button
              data-testid="reset-password-btn"
              onClick={handleResetPassword}
              className="flex items-center gap-2 px-4 py-2 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800 transition-colors"
            >
              <Key size={15} />
              Reestablecer contraseña
            </button>
            <p className="text-xs text-slate-500 mt-2">
              Se enviará un correo a {user?.email} con instrucciones para restablecer tu contraseña.
            </p>
          </div>
        </div>

        {/* Mercado Libre connection */}
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Conexión con Mercado Libre</h2>

          {meliStatus?.connected ? (
            <>
              <div className="flex items-center gap-2 text-emerald-400 mb-4">
                <CheckCircle2 size={18} />
                <span className="text-sm font-medium">
                  Cuenta conectada{meliStatus.account_name ? ` · ${meliStatus.account_name}` : ''}
                </span>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-slate-800/50">
                  <span className="text-slate-400">ID de vendedor</span>
                  <span className="text-white font-medium">{meliStatus.meli_user_id}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-800/50">
                  <span className="text-slate-400">País</span>
                  <span className="text-white font-medium">{meliStatus.country}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-400">Estado del acceso</span>
                  <span className={meliStatus.expired ? 'text-amber-400 font-medium' : 'text-emerald-400 font-medium'}>
                    {meliStatus.expired ? 'Expirado — reconecta' : 'Activo'}
                  </span>
                </div>
              </div>
              <button
                data-testid="reconnect-meli-btn"
                onClick={handleConnectMeli}
                className="flex items-center gap-2 px-4 py-2 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800 mt-6 transition-colors"
              >
                <Link2 size={15} />
                Reconectar cuenta
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-400 mb-5">
                Conecta tu cuenta de Mercado Libre para sincronizar tus ventas, publicaciones e inventario.
              </p>
              <button
                data-testid="connect-meli-btn"
                onClick={handleConnectMeli}
                className="flex items-center gap-2 px-5 py-2.5 bg-yellow-400 hover:bg-yellow-500 text-gray-900 rounded-lg text-sm font-semibold transition-colors"
              >
                <Link2 size={16} />
                Conectar con Mercado Libre
              </button>
            </>
          )}
        </div>

        {/* Diagnostics: surfaces exactly what MeLi answers for the metrics
            that are reporting zero, without needing to read server logs. */}
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-1">Conexión con Yuju</h2>
          <p className="text-sm text-slate-400 mb-4">
            Los títulos y descripciones que apruebes en Masivo actualizan los campos generales del producto en Yuju.
            La ficha técnica no: la API de Yuju no puede escribir atributos, así que ésa
            sigue yendo directo a Mercado Libre.
          </p>

          {yuju?.conectado ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-emerald-400">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                Conectado{yuju.conexion?.cuenta?.name ? ` · ${yuju.conexion.cuenta.name}` : ''}
                {yuju.conexion?.channel_id ? ` · canal ${yuju.conexion.channel_id}` : ''}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-slate-300">
                  {yuju.pendientes} publicaciones esperando envío
                </span>
                <button
                  onClick={pushToYuju}
                  disabled={pushing || !yuju.pendientes}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  {pushing ? 'Enviando...' : 'Enviar a Yuju'}
                </button>
                  <button
                    onClick={loadQueue}
                    disabled={pushing || queueLoading}
                    className="px-4 py-2 border border-amber-500/50 text-amber-300 rounded-lg text-sm font-medium disabled:opacity-50"
                  >
                    {queueLoading ? 'Cargando envíos...' : 'Ver detalle y reintentar envíos'}
                  </button>
              </div>
              <p className="text-xs text-slate-400">
                Puedes reenviar los textos guardados de los últimos 7 días, aunque antes
                aparecieran como enviados, junto con los pendientes. La aceptación de la API
                no confirma todavía su publicación en Mercado Libre. Los nuevos envíos y
                reintentos actualizan el título y la descripción generales en Yuju.
              </p>
              {queueItems !== null && (
                <div className="space-y-3 border-t border-slate-700 pt-4">
                  <p className="text-sm text-slate-300">{queueItems.length} registros guardados. Selecciona los que quieras reenviar.</p>
                  {queueItems.length === 500 && <p className="text-xs text-amber-300">Se muestran los 500 registros más recientes.</p>}
                  <div className="max-h-[32rem] overflow-y-auto space-y-3">
                    {queueItems.map(row => (
                      <div key={row.sku} className="border border-slate-700 rounded-lg p-3 space-y-2">
                        <label className="flex items-start gap-3 text-sm text-white">
                          <input type="checkbox" className="mt-1 accent-blue-500" disabled={pushing}
                            checked={selectedSkus.includes(row.sku)}
                            onChange={e => setSelectedSkus(current => e.target.checked
                              ? [...current, row.sku] : current.filter(sku => sku !== row.sku))} />
                          <span className="min-w-0 break-words">
                            SKU {shortSku(row.sku)} · {row.meli_id || 'Sin publicación'}
                            {shortSku(row.sku) !== row.sku && (
                              <span className="block text-xs text-slate-400">Referencia guardada: {row.sku}</span>
                            )}
                            <span className="block text-xs text-slate-400">
                              {row.estado === 'pendiente' ? 'Pendiente' : 'Marcado como enviado'}
                              {row.actualizado_at && ` · Guardado: ${new Date(row.actualizado_at).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' })} CDMX`}
                            </span>
                          </span>
                        </label>
                        <p className="text-sm text-slate-200 break-words"><span className="text-slate-400">Título: </span>{row.titulo || 'Sin cambio de título'}</p>
                        {row.descripcion ? (
                          <details className="text-sm text-slate-300">
                            <summary className="cursor-pointer text-blue-300">Ver descripción guardada</summary>
                            <p className="whitespace-pre-wrap break-words mt-2">{row.descripcion}</p>
                          </details>
                        ) : <p className="text-xs text-slate-400">Sin cambio de descripción</p>}
                        {row.error && <p className="text-xs text-red-300 break-words">Último error: {row.error}</p>}
                        {row.yuju_product_id && (
                          <p className="text-xs text-slate-400 break-words">
                            Último destino: producto Yuju {row.yuju_product_id}
                            {row.yuju_destino === 'general' ? ' · Campos generales' : ` · Campos por marketplace, canal ${row.yuju_channel_id || 'no registrado'}`}
                            {(row.confirmaciones || []).length > 0 && ` · Tienda(s): ${row.confirmaciones.map(c => c.id_shop).filter(Boolean).join(', ')}`}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                  <button onClick={retryRecentYuju} disabled={pushing || queueLoading || !selectedSkus.length}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm disabled:opacity-50">
                    {pushing ? 'Reenviando...' : `Reintentar ${selectedSkus.length} seleccionados`}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[['client_id', 'Client ID'], ['secret_key', 'Secret Key'],
                  ['channel_id', 'ID del canal de MeLi en Yuju']].map(([key, label]) => (
                  <div key={key}>
                    <label className="block text-xs text-slate-400 mb-1">{label}</label>
                    <input
                      type={key === 'secret_key' ? 'password' : 'text'}
                      value={yujuForm[key]}
                      onChange={e => setYujuForm(f => ({ ...f, [key]: e.target.value }))}
                      className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                ))}
              </div>
              <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-3">
                <p className="text-xs text-slate-400 mb-1">
                  En Yuju, la app debe apuntar aquí como URL de autenticación:
                </p>
                <code className="text-xs text-emerald-400 break-all">
                  {window.location.origin}/yuju/callback
                </code>
              </div>
              <button
                onClick={saveYujuCredentials}
                disabled={connecting || !yujuForm.client_id || !yujuForm.secret_key}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {connecting ? 'Guardando...' : yuju?.credenciales ? 'Actualizar credenciales' : 'Guardar credenciales'}
              </button>
              {yuju?.credenciales && (
                <p className="text-xs text-amber-400">
                  Credenciales guardadas. Falta que autorices MeliHub desde Yuju: al hacerlo
                  te regresa aquí y la conexión se completa sola.
                </p>
              )}
            </div>
          )}
        </div>

        {/* PriceHub integration: lets MeliHub look up historical costs and
            calculate withholding via PriceHub while enriching orders. */}
        <div className="bg-slate-900/50 border border-amber-500/20 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-1">Corrección automática de fotos</h2>
          <p className="text-sm text-slate-400 mb-4">
            Detecta problemas de fotos en Mercado Libre. En modo prueba deja el antes/después
            en Publicaciones para que apruebes cada subida.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="text-xs text-slate-400">
              Modo
              <select value={appSettings?.photo_fix_mode || 'prueba'}
                onChange={e => savePhotoFixSettings({ photo_fix_mode: e.target.value })}
                className="mt-1 w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white">
                <option value="prueba">Prueba · aprobar manualmente</option>
                <option value="encendido">Encendido · subir automáticamente</option>
              </select>
            </label>
            <label className="text-xs text-slate-400">
              Publicaciones por día
              <input type="number" min="1" max="500" value={appSettings?.photo_fix_daily_limit || 20}
                onChange={e => setAppSettings(s => ({ ...(s || {}), photo_fix_daily_limit: Number(e.target.value) }))}
                onBlur={e => savePhotoFixSettings({ photo_fix_daily_limit: Number(e.target.value) })}
                className="mt-1 w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white" />
            </label>
            <label className="text-xs text-slate-400">
              Tope AI diario (USD)
              <input type="number" min="0" step="0.01" value={appSettings?.photo_fix_daily_cost_usd ?? 5}
                onChange={e => setAppSettings(s => ({ ...(s || {}), photo_fix_daily_cost_usd: Number(e.target.value) }))}
                onBlur={e => savePhotoFixSettings({ photo_fix_daily_cost_usd: Number(e.target.value) })}
                className="mt-1 w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white" />
            </label>
          </div>
          <p className="text-xs text-slate-500 mt-3">Las correcciones de tamaño con Pillow no consumen el tope de IA.</p>
        </div>

        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-1">Integración PriceHub</h2>
          <p className="text-sm text-slate-400 mb-4">
            Datos de conexión para que MeliHub consulte costos por SKU y calcule
            retenciones en PriceHub al sincronizar órdenes.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">URL de PriceHub</label>
              <input
                type="text"
                value={pricehubForm.url}
                onChange={e => setPricehubForm(f => ({ ...f, url: e.target.value }))}
                className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Token</label>
              <input
                type="password"
                value={pricehubForm.token}
                onChange={e => setPricehubForm(f => ({ ...f, token: e.target.value }))}
                className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Canal</label>
              <input
                type="text"
                value={pricehubForm.canal}
                onChange={e => setPricehubForm(f => ({ ...f, canal: e.target.value }))}
                placeholder="E-CORP (Meli)"
                className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          <button
            onClick={savePricehubSettings}
            disabled={pricehubConfiguring || !pricehubForm.url}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {pricehubConfiguring ? 'Guardando...' : 'Guardar configuración'}
          </button>
          <label className="mt-5 flex items-center justify-between gap-4 rounded-lg border border-slate-700/70 bg-slate-800/30 p-4 cursor-pointer">
            <span>
              <span className="block text-sm font-medium text-white">COGS con IVA (igual que Selltrix y reportes de proveedor)</span>
              <span className="block text-xs text-slate-500 mt-1">Usa el costo IVA incluido en Dashboard, Rentabilidad, Analytics y exportaciones.</span>
            </span>
            <input
              type="checkbox"
              role="switch"
              aria-label="COGS con IVA (igual que Selltrix y reportes de proveedor)"
              checked={appSettings?.cogs_con_iva !== false}
              onChange={event => saveCogsSetting(event.target.checked)}
              className="h-5 w-5 accent-blue-500 shrink-0"
            />
          </label>
          <div className="mt-4 rounded-lg border border-slate-700/70 bg-slate-800/30 p-4">
            <p className="text-sm font-medium text-white">IVA acreditable usado en la estimación</p>
            <p className="text-xs text-slate-500 mt-1 mb-3">
              Por omisión se acredita el IVA incluido en COGS, comisiones y envío ML. Desactiva sólo un componente si no cuentas con CFDI o no aplica.
            </p>
            {[
              ['iva_acreditable_cogs', 'IVA de COGS'],
              ['iva_acreditable_comisiones', 'IVA de comisiones ML'],
              ['iva_acreditable_envio', 'IVA de envío ML'],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center justify-between py-1.5 text-sm text-slate-300 cursor-pointer">
                {label}
                <input
                  type="checkbox"
                  role="switch"
                  aria-label={label}
                  checked={appSettings?.[key] !== false}
                  onChange={event => saveIvaCreditSetting(key, event.target.checked)}
                  className="h-4 w-4 accent-blue-500"
                />
              </label>
            ))}
          </div>
        </div>

        <div className="bg-slate-900/50 border border-slate-800/50 rounded-2xl p-6">
          <h2 className="text-xl font-bold text-white mb-1">Acceso para PriceHub</h2>
          <p className="text-sm text-slate-400 mb-4">
            Una llave para que PriceHub lea de MeliHub los costos reales de venta —
            comisión, envío, retenciones y marketing por SKU — sin abrir una segunda
            conexión a Mercado Libre.
          </p>

          <button
            onClick={issueKey}
            disabled={issuing}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {issuing ? 'Generando...' : apiKey ? 'Generar una nueva' : 'Generar llave'}
          </button>

          {apiKey && (
            <div className="mt-4 space-y-2">
              {/* Shown once: it is not stored in the browser and the endpoint
                  never returns it again. */}
              <div className="flex items-center gap-2 bg-slate-950/60 border border-slate-800 rounded-lg px-3 py-2">
                <code className="text-xs text-emerald-400 break-all flex-1">{apiKey}</code>
                <button
                  onClick={() => { navigator.clipboard.writeText(apiKey); toast.success('Copiada'); }}
                  className="text-xs text-blue-400 hover:text-blue-300 shrink-0"
                >
                  Copiar
                </button>
              </div>
              <p className="text-xs text-amber-400">
                Cópiala ahora: no se vuelve a mostrar. Va en el header <code>X-API-Key</code>.
              </p>
              <p className="text-xs text-slate-500">
                Endpoints: <code>GET /api/partner/costs?days=90</code> ·{' '}
                <code>GET /api/partner/competition?sku=XXX</code>
              </p>
            </div>
          )}
        </div>

        <div className="bg-slate-900/50 border border-slate-800/50 rounded-2xl p-6">
          <h2 className="text-xl font-bold text-white mb-1">Diagnóstico de conexión</h2>
          <p className="text-sm text-slate-400 mb-4">
            Consulta qué responde Mercado Libre para visitas, publicidad y retenciones.
          </p>
          <div className="flex gap-3">
            <button
              data-testid="run-diagnostics-btn"
              onClick={handleDiagnostics}
              disabled={diagnosing}
              className="px-4 py-2 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800 disabled:opacity-50 transition-colors"
            >
              {diagnosing ? 'Consultando...' : 'Ejecutar diagnóstico'}
            </button>
            {diagnostics && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(diagnostics, null, 2));
                  toast.success('Diagnóstico copiado');
                }}
                className="px-4 py-2 border border-blue-500/30 rounded-lg text-sm text-blue-400 hover:bg-blue-500/10 transition-colors"
              >
                Copiar resultado
              </button>
            )}
          </div>
          {diagnostics && (
            <pre className="mt-4 text-xs text-slate-400 bg-slate-950/50 border border-slate-800 rounded-lg p-3 overflow-auto max-h-96">
              {JSON.stringify(diagnostics, null, 2)}
            </pre>
          )}
        </div>

        {/* Subscription */}
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4">Suscripción</h2>
          <div className="flex items-center justify-between py-3 border-b border-slate-800/50">
            <span className="text-sm text-slate-400">Plan actual</span>
            <span className="text-sm text-white font-medium">Suscripción no disponible</span>
          </div>

          <button
            data-testid="cancel-subscription-btn"
            onClick={handleCancelSubscription}
            disabled
            className="flex items-center gap-2 px-4 py-2 border border-red-500/30 rounded-lg text-sm text-red-400 hover:bg-red-500/10 mt-6 transition-colors"
          >
            <X size={15} />
            Cancelar suscripción
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}
